import { SaleOffer, BuyRequest } from '../models/Marketplace';
import { logMarketplaceActivity } from './adminActivityDatabase.ts';
import { getTrackingInfo } from '../utils/trackingUtils.ts';
import { getDisplayName, getUserById, getAllUsers } from './userDatabase.ts';
import { db } from '../config/firebase.ts';
import { collection, doc, getDocs, getDoc, setDoc, updateDoc, deleteDoc, query, where, orderBy } from 'firebase/firestore';

// Default mock data
const DEFAULT_SALE_OFFERS: SaleOffer[] = [
  {
    id: '1',
    sharePrice: '45.75',
    numberOfShares: '500',
    timestamp: '2023-07-15T10:30:00',
    confirmationTimestamp: '2023-07-15T10:32:15',
    legalOwnershipConfirmed: true,
    accuracyConfirmed: true,
    ipAddress: '192.168.1.1',
    country: 'United States',
    isp: 'Comcast',
    seller: '1',
    showFullName: true
  },
  {
    id: '2',
    sharePrice: '46.25',
    numberOfShares: '300',
    timestamp: '2023-07-16T11:20:00',
    confirmationTimestamp: '2023-07-16T11:22:30',
    legalOwnershipConfirmed: true,
    accuracyConfirmed: true,
    ipAddress: '192.168.1.2',
    country: 'Norway',
    isp: 'Telenor',
    seller: '2',
    showFullName: true
  }
];

const DEFAULT_BUY_REQUESTS: BuyRequest[] = [
  {
    id: '1',
    sharePrice: '44.50',
    numberOfShares: '200',
    requestTimestamp: '2023-07-14T09:25:00',
    confirmationTimestamp: '2023-07-14T09:27:45',
    sufficientFundsConfirmed: true,
    accuracyConfirmed: true,
    ipAddress: '192.168.1.5',
    country: 'United States',
    isp: 'AT&T',
    requester: '3',
    showFullName: true
  },
  {
    id: '2',
    sharePrice: '45.00',
    numberOfShares: '150',
    requestTimestamp: '2023-07-17T14:10:00',
    confirmationTimestamp: '2023-07-17T14:12:20',
    sufficientFundsConfirmed: true,
    accuracyConfirmed: true,
    ipAddress: '192.168.1.6',
    country: 'United States',
    isp: 'Verizon',
    requester: '4',
    showFullName: false
  }
];

// Initialize data from Firestore or defaults
let SALE_OFFERS: SaleOffer[] = [];
let BUY_REQUESTS: BuyRequest[] = [];

// Initialize Firestore collections
export const initializeMarketplace = async (): Promise<void> => {
  try {
    // Load sale offers
    const saleOffersQuery = query(collection(db, 'saleOffers'), orderBy('timestamp', 'desc'));
    const saleOffersSnapshot = await getDocs(saleOffersQuery);
    
    if (saleOffersSnapshot.empty) {
      // Initialize with defaults if empty
      for (const offer of DEFAULT_SALE_OFFERS) {
        await setDoc(doc(db, 'saleOffers', offer.id), offer);
      }
      SALE_OFFERS = [...DEFAULT_SALE_OFFERS];
    } else {
      SALE_OFFERS = saleOffersSnapshot.docs.map(doc => doc.data() as SaleOffer);
    }

    // Load buy requests
    const buyRequestsQuery = query(collection(db, 'buyRequests'), orderBy('requestTimestamp', 'desc'));
    const buyRequestsSnapshot = await getDocs(buyRequestsQuery);
    
    if (buyRequestsSnapshot.empty) {
      // Initialize with defaults if empty
      for (const request of DEFAULT_BUY_REQUESTS) {
        await setDoc(doc(db, 'buyRequests', request.id), request);
      }
      BUY_REQUESTS = [...DEFAULT_BUY_REQUESTS];
    } else {
      BUY_REQUESTS = buyRequestsSnapshot.docs.map(doc => doc.data() as BuyRequest);
    }
  } catch (error) {
    console.error('Failed to initialize marketplace:', error);
    // Fallback to defaults
    SALE_OFFERS = [...DEFAULT_SALE_OFFERS];
    BUY_REQUESTS = [...DEFAULT_BUY_REQUESTS];
  }
};

// Sale Offer functions
export const getAllSaleOffers = (): SaleOffer[] => {
  return [...SALE_OFFERS];
};

export const addSaleOffer = async (offer: SaleOffer): Promise<void> => {
  try {
    await setDoc(doc(db, 'saleOffers', offer.id), offer);
    
    SALE_OFFERS.push(offer);
    
    // Track the activity
    const user = await getUserById(offer.seller);
    const trackingInfo = getTrackingInfo(offer.seller);
    
    logMarketplaceActivity(
      offer.seller,
      user ? getDisplayName(user) : offer.seller,
      'create_sale_offer',
      offer.id,
      'sale_offer',
      {
        sharePrice: offer.sharePrice,
        numberOfShares: offer.numberOfShares
      }
    );
  } catch (error) {
    console.error('Failed to add sale offer:', error);
    throw error;
  }
};

export const deleteSaleOffer = async (offerId: string): Promise<boolean> => {
  try {
    // First check if the offer exists in Firestore
    const offerDoc = await getDoc(doc(db, 'saleOffers', offerId));
    if (!offerDoc.exists()) {
      return false;
    }
    
    const offer = offerDoc.data() as SaleOffer;
    
    // Delete from Firestore
    await deleteDoc(doc(db, 'saleOffers', offerId));
    
    // Update local state if it exists
    const localOfferIndex = SALE_OFFERS.findIndex(o => o.id === offerId);
    if (localOfferIndex !== -1) {
      SALE_OFFERS.splice(localOfferIndex, 1);
    }
    
    // Track the activity
    const user = await getUserById(offer.seller);
    const trackingInfo = getTrackingInfo(offer.seller);
    
    logMarketplaceActivity(
      offer.seller,
      user ? getDisplayName(user) : offer.seller,
      'delete_sale_offer',
      offer.id,
      'sale_offer',
      {
        sharePrice: offer.sharePrice,
        numberOfShares: offer.numberOfShares
      }
    );
    
    return true;
  } catch (error) {
    console.error('Failed to delete sale offer:', error);
    throw error;
  }
};

export const getSaleOfferById = async (offerId: string): Promise<SaleOffer | undefined> => {
  try {
    const offerDoc = await getDoc(doc(db, 'saleOffers', offerId));
    if (offerDoc.exists()) {
      return offerDoc.data() as SaleOffer;
    }
    return undefined;
  } catch (error) {
    console.error('Failed to get sale offer by ID:', error);
    return undefined;
  }
};

// Buy Request functions
export const getAllBuyRequests = (): BuyRequest[] => {
  return [...BUY_REQUESTS];
};

export const addBuyRequest = async (request: BuyRequest): Promise<void> => {
  try {
    await setDoc(doc(db, 'buyRequests', request.id), request);
    
    BUY_REQUESTS.push(request);
    
    // Track the activity
    const user = await getUserById(request.requester);
    const trackingInfo = getTrackingInfo(request.requester);
    
    logMarketplaceActivity(
      request.requester,
      user ? getDisplayName(user) : request.requester,
      'create_buy_request',
      request.id,
      'buy_request',
      {
        sharePrice: request.sharePrice,
        numberOfShares: request.numberOfShares
      }
    );
  } catch (error) {
    console.error('Failed to add buy request:', error);
    throw error;
  }
};

export const deleteBuyRequest = async (requestId: string): Promise<boolean> => {
  try {
    // First check if the request exists in Firestore
    const requestDoc = await getDoc(doc(db, 'buyRequests', requestId));
    if (!requestDoc.exists()) {
      return false;
    }
    
    const request = requestDoc.data() as BuyRequest;
    await deleteDoc(doc(db, 'buyRequests', requestId));
    
    // Update local state if it exists
    const localRequestIndex = BUY_REQUESTS.findIndex(r => r.id === requestId);
    if (localRequestIndex !== -1) {
      BUY_REQUESTS.splice(localRequestIndex, 1);
    }
    
    // Track the activity
    const user = await getUserById(request.requester);
    const trackingInfo = getTrackingInfo(request.requester);
    
    logMarketplaceActivity(
      request.requester,
      user ? getDisplayName(user) : request.requester,
      'delete_buy_request',
      request.id,
      'buy_request',
      {
        sharePrice: request.sharePrice,
        numberOfShares: request.numberOfShares
      }
    );
    
    return true;
  } catch (error) {
    console.error('Failed to delete buy request:', error);
    throw error;
  }
};

export const getBuyRequestById = async (requestId: string): Promise<BuyRequest | undefined> => {
  try {
    const requestDoc = await getDoc(doc(db, 'buyRequests', requestId));
    if (requestDoc.exists()) {
      return requestDoc.data() as BuyRequest;
    }
    return undefined;
  } catch (error) {
    console.error('Failed to get buy request by ID:', error);
    return undefined;
  }
};

// Utility functions
export const resetMarketplaceToDefaults = async (): Promise<void> => {
  try {
    // Delete all existing data
    const collections = ['saleOffers', 'buyRequests'];
    for (const collectionName of collections) {
      const querySnapshot = await getDocs(collection(db, collectionName));
      const deletePromises = querySnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
    }
    
    // Add default data
    const addPromises = [
      ...DEFAULT_SALE_OFFERS.map(offer => setDoc(doc(db, 'saleOffers', offer.id), offer)),
      ...DEFAULT_BUY_REQUESTS.map(request => setDoc(doc(db, 'buyRequests', request.id), request))
    ];
    await Promise.all(addPromises);
    
    SALE_OFFERS = [...DEFAULT_SALE_OFFERS];
    BUY_REQUESTS = [...DEFAULT_BUY_REQUESTS];
  } catch (error) {
    console.error('Failed to reset marketplace to defaults:', error);
    throw error;
  }
};

// Reset marketplace data to apply fixes for username to user ID migration
export const applyMarketplaceFixes = (): void => {
  // Check if we need to apply fixes by looking for old data using usernames
  const currentSaleOffers = getAllSaleOffers();
  const currentBuyRequests = getAllBuyRequests();
  
  const hasOldData = currentSaleOffers.some(offer => 
    offer.seller === 'user' ||
    offer.seller === 'havar' ||
    offer.seller === 'endre' ||
    offer.seller === 'user3' ||
    offer.seller.includes(' ') // Contains spaces (likely full name)
  ) || currentBuyRequests.some(request => 
    request.requester === 'user' ||
    request.requester === 'havar' ||
    request.requester === 'endre' ||
    request.requester === 'user3' ||
    request.requester.includes(' ') // Contains spaces (likely full name)
  );
  
  if (hasOldData) {
    resetMarketplaceToDefaults();
  }
};



// Migration function to fix username to user ID inconsistencies
export const migrateMarketplaceUsernamesToUserIds = async (): Promise<void> => {
  try {
    // Get all users to create a mapping
    const users = await getAllUsers();
    const usernameToIdMap = new Map<string, string>();
    users.forEach((user: any) => {
      usernameToIdMap.set(user.username, user.id);
    });
    
    let migrationCount = 0;
    
    // Migrate sale offers
    for (const offer of SALE_OFFERS) {
      const userId = usernameToIdMap.get(offer.seller);
      if (userId && userId !== offer.seller) {
        offer.seller = userId;
        await setDoc(doc(db, 'saleOffers', offer.id), offer);
        migrationCount++;
      }
    }
    
    // Migrate buy requests
    for (const request of BUY_REQUESTS) {
      const userId = usernameToIdMap.get(request.requester);
      if (userId && userId !== request.requester) {
        request.requester = userId;
        await setDoc(doc(db, 'buyRequests', request.id), request);
        migrationCount++;
      }
    }
  } catch (error) {
    throw error;
  }
}; 